import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { RootState } from 'store';
import { clearUser, setToken } from 'store/user/userSlice';

import {
  FetchAllPagesOptions,
  FetchPaginatedDataOptions,
  PaginatedResponse
} from '../models/services.types';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL,
  prepareHeaders: async (headers, api) => {
    try {
      // Re-Auth happens automatically when the token expires

      // Type guard to ensure `api` conforms to `BaseQueryApi`
      const isBaseQueryApi = (obj: unknown): obj is BaseQueryApi =>
        typeof obj === 'object' &&
        obj !== null &&
        'signal' in obj &&
        'abort' in obj &&
        'dispatch' in obj;

      const session = await fetchAuthSession();
      if (session?.tokens) {
        const accessToken = session?.tokens.accessToken?.toString() ?? '';
        const idToken = session?.tokens.idToken?.toString() ?? '';

        // Set the token in the store to trigger pre-existing functionality
        if (isBaseQueryApi(api)) {
          const api2 = api;
          if ((api2.getState() as RootState).user.accessToken !== accessToken) {
            api2.dispatch(setToken({ accessToken, idToken }));
          }
        }

        headers.set('Authorization', 'Bearer ' + accessToken);
        headers.set('idToken', idToken);
      }
    } catch (e) {
      if (e !== 'The user is not authenticated')
        console.error('Unable to fetch current session', e);
    } finally {
      headers.set('API-KEY', headers.get('API-KEY') || import.meta.env.VITE_API_KEY || '');
      headers.set('Platform', 'web-physician');
    }

    return headers;
  }
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  try {
    // Refresh token if user logged in
    // Note: aws-amplify no longer returns refreshToken or clockDrift with the AuthSession. These values are used under the hood to refresh/validate the user session
    if (result.error && result.error.status === 401) {
      const refreshedSession = await fetchAuthSession({ forceRefresh: true });
      if (!refreshedSession.tokens) {
        api.dispatch(clearUser());
        return result;
      }

      const refreshedAccessToken = refreshedSession.tokens?.accessToken?.toString() ?? '';
      const refreshedIdToken = refreshedSession.tokens?.idToken?.toString() ?? '';

      api.dispatch(setToken({ accessToken: refreshedAccessToken, idToken: refreshedIdToken }));

      // Retry the original request with the new token
      const headers = new Headers(((args as FetchArgs)?.headers as HeadersInit) ?? {});
      headers.set('Authorization', 'Bearer ' + refreshedAccessToken);
      headers.set('idToken', refreshedIdToken);

      // Retry the request with updated headers
      result = await baseQuery({ ...(args as FetchArgs), headers }, api, extraOptions);
      if (result.error && result.error.status === 401) {
        // If the request fails with 401 again, clear user data
        api.dispatch(clearUser());
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Clear user data if user logged out
    api.dispatch(clearUser());
  }

  return result;
};

const extractItemId = (item: unknown): string | undefined => {
  if (item && typeof item === 'object') {
    const record = item as Record<string, unknown>;
    if (typeof record._id === 'string') return record._id;
    if (typeof record.id === 'string') return record.id;
  }
  return undefined;
};

const extractTotalCount = (info: unknown): number | undefined => {
  if (info && typeof info === 'object' && 'totalCount' in info) {
    const { totalCount } = info as { totalCount?: unknown };
    if (typeof totalCount === 'number' && Number.isFinite(totalCount)) {
      return totalCount;
    }
  }
  return undefined;
};

export const fetchAllPages = async <
  Item,
  Response extends PaginatedResponse<Item>,
  ExtraOptions = {}
>(
  options: FetchAllPagesOptions<Item, Response, ExtraOptions>
): Promise<{ data: Response } | { error: FetchBaseQueryError }> => {
  const {
    api,
    baseQuery,
    createEmptyResponse,
    disableRequestLimit = false,
    extraOptions,
    limit = 50,
    pageNo = 0,
    params,
    url
  } = options;

  if (limit <= 0) {
    return { data: createEmptyResponse() };
  }

  const items: Item[] = [];
  const seenIds = new Set<string>();
  let currentPage = pageNo;
  let lastResponse: Response | undefined;
  let latestTotalCount: number | undefined;
  let requestCount = 0;
  const maxRequests = 10;

  while (true) {
    // Enforce maximum request limit (10 requests) unless disabled
    if (!disableRequestLimit && requestCount >= maxRequests) {
      console.error(
        `fetchAllPages: Maximum request limit of ${maxRequests} reached for ${url}. ` +
          `Consider using disableRequestLimit: true if this is expected.`
      );
      break;
    }

    const request = { params: { ...params, limit, pageNo: currentPage }, url } satisfies FetchArgs;

    const result = await baseQuery(request, api, extraOptions);
    requestCount += 1;

    if (result.error) {
      return { error: result.error };
    }

    const payload = result.data as Response | undefined;
    if (!payload) break;

    lastResponse = payload;
    const pageItems = payload.data ?? [];
    const beforeLength = items.length;

    for (const item of pageItems) {
      const itemId = extractItemId(item);

      if (itemId) {
        if (!seenIds.has(itemId)) {
          seenIds.add(itemId);
          items.push(item);
        }
      } else {
        items.push(item);
      }
    }

    const totalFromInfo = extractTotalCount(payload.info);
    if (totalFromInfo !== undefined) {
      latestTotalCount = totalFromInfo;
    }

    const noNewItems = items.length === beforeLength;
    const reachedTotal = latestTotalCount !== undefined && items.length >= latestTotalCount;
    const isLastPage = !pageItems.length || pageItems.length < limit || reachedTotal || noNewItems;

    if (isLastPage) {
      break;
    }

    currentPage += 1;
  }

  const baseResponse = lastResponse ?? createEmptyResponse();
  const totalCount = Math.max(latestTotalCount ?? 0, items.length);

  const mergedInfo =
    baseResponse.info && typeof baseResponse.info === 'object'
      ? { ...(baseResponse.info as Record<string, unknown>), totalCount }
      : { totalCount };

  return {
    data: {
      ...baseResponse,
      data: items,
      info: mergedInfo as Response['info']
    }
  };
};

export const fetchPaginatedData = async <
  Item,
  Response extends PaginatedResponse<Item>,
  ExtraOptions = {}
>(
  options: FetchPaginatedDataOptions<Item, Response, ExtraOptions>
): Promise<{ data: Response } | { error: FetchBaseQueryError }> => {
  const {
    api,
    baseQuery,
    createEmptyResponse,
    disableRequestLimit,
    extraOptions,
    limit = 50,
    pageNo = 0,
    params,
    shouldFetchAllPages = false,
    url
  } = options;

  if (!shouldFetchAllPages) {
    const request = { params: { ...params, limit, pageNo }, url } satisfies FetchArgs;
    const result = await baseQuery(request, api, extraOptions);

    if (result.error) {
      return { error: result.error };
    }

    const payload = result.data as Response | undefined;
    return { data: payload ?? createEmptyResponse() };
  }

  const wrappedBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    ExtraOptions
  > = (queryArg, queryApi, queryExtraOptions) => baseQuery(queryArg, queryApi, queryExtraOptions);

  return fetchAllPages<Item, Response, ExtraOptions>({
    api,
    baseQuery: wrappedBaseQuery,
    createEmptyResponse,
    disableRequestLimit,
    extraOptions,
    limit,
    pageNo,
    params,
    url
  });
};
